<script lang="ts">
  import type { InkEngine } from '../ink/engine';

  let { engine, onclose }: { engine: InkEngine; onclose: () => void } = $props();

  // Detents: the page's distinct event timestamps (engine samples to ≤200).
  let stops = $state<number[]>([]);
  let idx = $state(0);
  let ready = $state(false);
  let busy = $state(false);
  let finished = false;

  $effect(() => {
    let cancelled = false;
    void engine.enterHistoryPreview().then((info) => {
      // Unmounted while loading: the cleanup ran before preview mode was
      // actually entered, so its exit was a no-op — exit here instead.
      if (cancelled) { void engine.exitHistoryPreview(); return; }
      stops = info.events.length ? info.events : [info.max];
      idx = stops.length - 1; // start at "now" (matches the live canvas)
      ready = true;
    });
    return () => {
      cancelled = true;
      // Unmount without Restore/Cancel (page nav, remote purge): leave
      // preview so a surviving engine gets its live strokes back.
      // exitHistoryPreview is a safe no-op if already exited or destroyed.
      if (!finished) void engine.exitHistoryPreview();
    };
  });

  function scrub(e: Event) {
    idx = Number((e.currentTarget as HTMLInputElement).value);
    engine.previewAsOf(stops[idx]!);
  }

  async function restore() {
    if (busy || !ready) return;
    busy = true;
    finished = true;
    await engine.restoreToTime(stops[idx]!); // exits preview itself, one undo op
    onclose();
  }

  async function cancel() {
    if (busy) return;
    busy = true;
    finished = true;
    await engine.exitHistoryPreview();
    onclose();
  }
</script>

<div class="history-bar">
  {#if ready}
    <span class="label">{new Date(stops[idx]!).toLocaleString()}</span>
    <input
      type="range"
      min="0"
      max={stops.length - 1}
      step="1"
      value={idx}
      oninput={scrub}
      aria-label="History position"
    />
    <button class="primary" onclick={restore} disabled={busy}>Restore</button>
    <button onclick={cancel} disabled={busy}>Cancel</button>
  {:else}
    <span class="label">Loading history…</span>
  {/if}
</div>

<style>
  .history-bar {
    position: absolute; left: 12px; right: 12px; bottom: max(12px, env(safe-area-inset-bottom));
    z-index: 6; display: flex; align-items: center; gap: 12px;
    background: var(--surface); border-radius: var(--radius-panel); box-shadow: var(--shadow-soft);
    padding: 10px 14px;
  }
  .label { font-size: 12px; opacity: 0.75; min-width: 130px; }
  input[type='range'] { flex: 1; min-width: 80px; accent-color: var(--accent); }
  button {
    border: 1px solid var(--hairline); background: var(--paper); color: var(--ink-primary);
    border-radius: var(--radius-panel); padding: 8px 14px; min-height: 40px; cursor: pointer;
  }
  button.primary { background: var(--accent); color: #fff; border: none; }
  button:disabled { opacity: 0.5; cursor: default; }
</style>
